import type { ChangelogEntry, ChangelogTag } from '@/lib/changelog';
import { cn } from '@/lib/utils';
import { X } from 'lucide-react';
import { Fragment, useEffect } from 'react';

/**
 * Right-side drawer listing every release from docs/changelog.md, newest
 * first, grouped by date with tag chips per change.
 *
 * Presentation only — the AppShell owns open/close + the "seen" bookkeeping
 * (see whats_new.ts) so this component stays trivially testable.
 */

const TAG_LABELS: Record<ChangelogTag, string> = {
  boarding: 'Boarding',
  daycare: 'Daycare',
  grooming: 'Grooming',
  payments: 'Payments',
  portal: 'Customer portal',
  settings: 'Settings',
  staff: 'Staff app',
};

const DATE_FMT = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  timeZone: 'UTC',
});

function formatReleaseDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  return Number.isNaN(d.getTime()) ? iso : DATE_FMT.format(d);
}

interface Props {
  open: boolean;
  entries: ReadonlyArray<ChangelogEntry>;
  /** Newest date the user had seen BEFORE opening — entries newer than this get a "New" marker. */
  lastSeen: string | null;
  onClose: () => void;
}

export function WhatsNewDrawer({ open, entries, lastSeen, onClose }: Props) {
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" aria-label="What's new">
      <button
        type="button"
        aria-label="Close what's new"
        className="absolute inset-0 bg-foreground/40"
        onClick={onClose}
      />
      <aside className="absolute inset-y-0 right-0 flex w-full max-w-md flex-col bg-background shadow-2xl ring-1 ring-border">
        <header className="flex items-start justify-between border-b border-border px-5 py-4">
          <div>
            <p className="text-[11px] font-medium uppercase tracking-[0.2em] text-muted-foreground">
              Release notes
            </p>
            <h2 className="font-heading text-lg font-medium tracking-tight">What's new</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="grid size-8 place-items-center rounded-lg text-muted-foreground hover:bg-secondary press"
          >
            <X className="size-4" strokeWidth={1.75} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto">
          {entries.length === 0 ? (
            <p className="px-5 py-8 text-sm text-muted-foreground">No release notes yet.</p>
          ) : (
            <ol className="divide-y divide-border">
              {entries.map((entry, idx) => {
                const isNew = lastSeen === null || entry.date > lastSeen;
                return (
                  <li
                    key={`${entry.date}-${idx}`}
                    className="rise px-5 py-4"
                    style={{ ['--i' as string]: idx }}
                  >
                    <div className="flex items-baseline justify-between gap-3">
                      <h3 className="text-sm font-medium">{entry.title}</h3>
                      <div className="flex shrink-0 items-center gap-2">
                        {isNew && (
                          <span className="inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-[10.5px] font-medium uppercase tracking-[0.14em] text-primary ring-hairline">
                            New
                          </span>
                        )}
                        <time dateTime={entry.date} className="num text-xs text-muted-foreground">
                          {formatReleaseDate(entry.date)}
                        </time>
                      </div>
                    </div>
                    <ul className="mt-2.5 space-y-2.5">
                      {entry.items.map((item, i) => (
                        <li key={i} className="text-sm leading-relaxed text-foreground/90">
                          {item.tags.length > 0 && (
                            <span className="mr-1.5 inline-flex flex-wrap gap-1 align-middle">
                              {item.tags.map((tag) => (
                                <Fragment key={tag}>
                                  <span
                                    className={cn(
                                      'inline-flex items-center rounded-full bg-secondary px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-[0.12em] text-muted-foreground ring-hairline',
                                    )}
                                  >
                                    {TAG_LABELS[tag]}
                                  </span>
                                </Fragment>
                              ))}
                            </span>
                          )}
                          {item.text}
                        </li>
                      ))}
                    </ul>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      </aside>
    </div>
  );
}
